import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  UserPlus,
  Mail,
  Phone,
  IdCard,
  User,
  Loader2,
  Download,
  CheckCircle2,
  ArrowRight,
  QrCode,
  LogIn,
} from 'lucide-react';
import Footer from '../components/Footer';
import { saveEcoparqueSession } from '../utils/ecoparqueSession';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function EcoparqueRegistro() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    nombre: '',
    email: '',
    telefono: '',
    dni: '',
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // { user, qr_png_base64 }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nombre || !form.email) {
      toast.error('Nombre y email son obligatorios');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/ecoparque/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nombre: form.nombre.trim(),
          email: form.email.trim().toLowerCase(),
          telefono: form.telefono || null,
          dni: form.dni || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || 'No se pudo completar el registro');
        return;
      }
      setResult(data);
      saveEcoparqueSession(data.user);
      toast.success(`¡Bienvenido/a ${data.user.nombre}! Tu código es ${data.user.user_code}`);
    } catch (err) {
      console.error(err);
      toast.error('Error de red. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadQR = () => {
    if (!result?.qr_png_base64) return;
    const a = document.createElement('a');
    a.href = result.qr_png_base64;
    a.download = `ecoparque-${result.user.user_code}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  /* -------------------- Éxito -------------------- */
  if (result) {
    return (
      <div className="min-h-screen bg-[#FAFCFE]">
        <Helmet>
          <title>Registro completado | Mi Ecoparque</title>
        </Helmet>
        <section className="pt-24 pb-12 px-6" style={{ background: 'linear-gradient(135deg, #1F8A70 0%, #1E5AA8 100%)' }}>
          <div className="max-w-3xl mx-auto text-center">
            <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 size={32} className="text-white" />
            </div>
            <h1 className="font-display text-4xl sm:text-5xl text-white mb-3">Registro completado</h1>
            <p className="text-white/80 max-w-xl mx-auto">
              Guarda tu código y QR. Los necesitarás cada vez que acudas al Ecoparque.
            </p>
          </div>
        </section>

        <section className="py-12 px-6">
          <div className="max-w-3xl mx-auto">
            <div className="rounded-3xl border border-[#CFE3F5] bg-white p-8 shadow-sm">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                <div className="flex flex-col items-center">
                  <div className="rounded-2xl border-2 border-[#CFE3F5] p-4 bg-white" data-testid="eco-registro-qr-wrapper">
                    <img
                      src={result.qr_png_base64}
                      alt={`QR Ecoparque ${result.user.user_code}`}
                      className="w-56 h-56"
                      data-testid="eco-registro-qr-img"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleDownloadQR}
                    className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1E5AA8] text-white text-sm font-medium hover:bg-[#0F4C97] transition-colors"
                    data-testid="eco-registro-download-qr-btn"
                  >
                    <Download size={16} />
                    Descargar QR
                  </button>
                </div>
                <div>
                  <p className="text-xs text-[#6F8AA8] uppercase tracking-wider mb-1">Tu código de usuario</p>
                  <p
                    className="font-display text-4xl text-[#0A2540] tabular-nums mb-4 select-all"
                    data-testid="eco-registro-user-code"
                  >
                    {result.user.user_code}
                  </p>
                  <p className="text-sm text-[#6F8AA8] mb-6">
                    <strong className="text-[#0A2540]">{result.user.nombre}</strong>
                    <br />
                    {result.user.email}
                  </p>
                  <div className="flex flex-col gap-3">
                    <button
                      type="button"
                      onClick={() => navigate('/ecoparque')}
                      className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#1F8A70] text-white text-sm font-medium hover:bg-[#177A60] transition-colors"
                      data-testid="eco-registro-go-dashboard-btn"
                    >
                      Ir a mi portal
                      <ArrowRight size={16} />
                    </button>
                    <Link
                      to="/ecoparque/login"
                      className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-[#CFE3F5] text-[#0A2540] text-sm font-medium hover:bg-[#F5F9FC] transition-colors"
                    >
                      <LogIn size={16} />
                      Acceder después con código
                    </Link>
                  </div>
                </div>
              </div>
              <div className="mt-8 p-4 rounded-2xl bg-[#F5F9FC] border border-[#CFE3F5] text-sm text-[#6F8AA8]">
                <strong className="text-[#0A2540]">Consejo:</strong> descarga el QR en tu móvil o imprímelo.
                El personal del Ecoparque lo escaneará para asociar tus entregas.
              </div>
            </div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  /* -------------------- Formulario -------------------- */
  return (
    <div className="min-h-screen bg-[#FAFCFE]">
      <Helmet>
        <title>Registro Mi Ecoparque | Cuidemos Los Alcázares®</title>
        <meta
          name="description"
          content="Regístrate gratis en Mi Ecoparque y obtén tu código QR personal para identificarte al entregar residuos en el Punto Limpio de Los Alcázares."
        />
      </Helmet>

      <section className="pt-24 pb-12 px-6" style={{ background: 'linear-gradient(135deg, #1F8A70 0%, #1E5AA8 100%)' }}>
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <UserPlus size={26} className="text-white" />
            </div>
            <span className="text-white/90 text-sm font-medium uppercase tracking-wider">Ciudadanía</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl text-white mb-3">Regístrate en Mi Ecoparque</h1>
          <p className="text-white/80 max-w-xl">
            Obtén tu <strong>código de usuario</strong> y tu <strong>QR personal</strong> para identificarte al entregar residuos.
          </p>
        </div>
      </section>

      <section className="py-12 px-6">
        <div className="max-w-2xl mx-auto">
          <form
            onSubmit={handleSubmit}
            className="rounded-3xl border border-[#CFE3F5] bg-white p-6 sm:p-8 shadow-sm"
            data-testid="eco-registro-form"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Nombre completo*" icon={User}>
                <input
                  type="text"
                  name="nombre"
                  value={form.nombre}
                  onChange={handleChange}
                  required
                  minLength={2}
                  className="w-full pl-10 pr-4 py-3 rounded-xl border border-[#CFE3F5] focus:outline-none focus:ring-2 focus:ring-[#1E5AA8] text-[#0A2540]"
                  placeholder="Ej: Juan Pérez"
                  data-testid="eco-registro-nombre"
                />
              </Field>
              <Field label="Email*" icon={Mail}>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                  className="w-full pl-10 pr-4 py-3 rounded-xl border border-[#CFE3F5] focus:outline-none focus:ring-2 focus:ring-[#1E5AA8] text-[#0A2540]"
                  placeholder="nombre@email.com"
                  data-testid="eco-registro-email"
                />
              </Field>
              <Field label="Teléfono" icon={Phone}>
                <input
                  type="tel"
                  name="telefono"
                  value={form.telefono}
                  onChange={handleChange}
                  className="w-full pl-10 pr-4 py-3 rounded-xl border border-[#CFE3F5] focus:outline-none focus:ring-2 focus:ring-[#1E5AA8] text-[#0A2540]"
                  placeholder="600 000 000"
                  data-testid="eco-registro-telefono"
                />
              </Field>
              <Field label="DNI/NIE" icon={IdCard}>
                <input
                  type="text"
                  name="dni"
                  value={form.dni}
                  onChange={handleChange}
                  className="w-full pl-10 pr-4 py-3 rounded-xl border border-[#CFE3F5] focus:outline-none focus:ring-2 focus:ring-[#1E5AA8] text-[#0A2540]"
                  placeholder="12345678X"
                  data-testid="eco-registro-dni"
                />
              </Field>
            </div>

            <p className="mt-4 text-xs text-[#6F8AA8]">
              Al registrarte aceptas el tratamiento de tus datos con la única finalidad de gestionar las entregas del Ecoparque,
              conforme al <Link to="/ciudadania" className="underline hover:text-[#1E5AA8]">aviso legal y política de privacidad</Link>.
            </p>

            <div className="flex flex-col sm:flex-row items-center gap-3 mt-6">
              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#1E5AA8] text-white text-sm font-medium hover:bg-[#0F4C97] transition-colors disabled:opacity-60"
                data-testid="eco-registro-submit-btn"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <QrCode size={16} />}
                {loading ? 'Creando tu usuario…' : 'Generar mi código y QR'}
              </button>
              <Link
                to="/ecoparque/login"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-[#CFE3F5] text-[#0A2540] text-sm font-medium hover:bg-[#F5F9FC] transition-colors"
                data-testid="eco-registro-go-login-link"
              >
                <LogIn size={16} />
                Ya tengo un código
              </Link>
            </div>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function Field({ label, icon: Icon, children }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-[#0A2540] uppercase tracking-wider mb-1.5 block">{label}</span>
      <div className="relative">
        <Icon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7DB9E8]" />
        {children}
      </div>
    </label>
  );
}
